/**
 * Embedded context window & compaction settings panel.
 * Rendered as a tab on the Agent detail page.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  Typography,
  Alert,
} from '@mui/material';
import { TextField } from '@core/components/mui';
import { contextWindowConfigService } from '../../services/contextWindowConfigService';
import { agentService } from '../../services/agentService';
import {
  ContextSummarizationStrategy,
  SummarizerType,
  type UpdateContextWindowConfigRequest,
} from '../../types/contextWindow';
import type {
  AgentDefinitionDto,
  AgentImageContextMode,
  UpdateAgentParameterRequest,
  UpdateAgentRequest,
} from '../../types/agent';
import { useNotificationStore } from '../../store/notificationStore';

interface AgentContextWindowPanelProps {
  agent: AgentDefinitionDto;
  readOnly?: boolean;
}

const DEFAULT_FORM: UpdateContextWindowConfigRequest = {
  maxContextTokens: 32000,
  summarizationThreshold: 28000,
  summarizationStrategy: ContextSummarizationStrategy.LastXMinusY,
  summarizerType: SummarizerType.Standard,
  customSummarizerPrompt: null,
  messagesToRetain: 10,
  messagesToSummarize: 50,
  enableAutoSummarization: true,
  compressionRatio: 0.3,
  priorityMessagesJson: null,
};

export const AgentContextWindowPanel = ({ agent, readOnly = false }: AgentContextWindowPanelProps) => {
  const { addNotification } = useNotificationStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageContextMode, setImageContextMode] = useState<AgentImageContextMode>(
    agent.imageContextMode ?? 'ocrToContext',
  );
  const [form, setForm] = useState<UpdateContextWindowConfigRequest>(DEFAULT_FORM);

  const canSave = useMemo(() => {
    if (readOnly) return false;
    if (form.maxContextTokens < 1024) return false;
    if (form.summarizationThreshold < 1 || form.summarizationThreshold > form.maxContextTokens) return false;
    if (form.compressionRatio <= 0 || form.compressionRatio >= 1) return false;
    if (form.summarizerType === SummarizerType.Custom && !form.customSummarizerPrompt?.trim()) return false;
    return true;
  }, [readOnly, form]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await contextWindowConfigService.getConfig(agent.id);
        if (!isMounted) return;
        setForm({
          maxContextTokens: cfg.maxContextTokens,
          summarizationThreshold: cfg.summarizationThreshold,
          summarizationStrategy: cfg.summarizationStrategy,
          summarizerType: cfg.summarizerType,
          customSummarizerPrompt: cfg.customSummarizerPrompt ?? null,
          messagesToRetain: cfg.messagesToRetain,
          messagesToSummarize: cfg.messagesToSummarize,
          enableAutoSummarization: cfg.enableAutoSummarization,
          compressionRatio: cfg.compressionRatio,
          priorityMessagesJson: cfg.priorityMessagesJson ?? null,
        });
        setImageContextMode(agent.imageContextMode ?? 'ocrToContext');
      } catch (err: any) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message || err?.message || 'Failed to load context window configuration',
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [agent.id, agent.imageContextMode]);

  const handleSave = async () => {
    if (!canSave) {
      addNotification('Please fix validation errors before saving', 'warning');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await contextWindowConfigService.updateConfig(agent.id, form);
      const updatePayload: UpdateAgentRequest = {
        name: agent.name,
        slug: agent.slug,
        description: agent.description,
        systemPrompt: agent.systemPrompt,
        category: agent.category,
        iconName: agent.iconName,
        sortOrder: agent.sortOrder,
        includeAllDefaultTools: agent.includeAllDefaultTools,
        fanOut: agent.fanOut,
        useOpenAiHostedSkills: agent.useOpenAiHostedSkills,
        injectSkillInstructionsAsUserContext: agent.injectSkillInstructionsAsUserContext,
        imageContextMode,
        isActive: agent.isActive,
        contextLimitLastNMessages: agent.contextLimitLastNMessages ?? null,
        contextLimitLastNDays: agent.contextLimitLastNDays ?? null,
        defaultModelId: agent.defaultModelId,
        parameters: agent.parameters.map((p) => ({
          id: p.id,
          key: p.key,
          label: p.label,
          description: p.description,
          type: p.type,
          defaultValue: p.defaultValue,
          isRequired: p.isRequired,
          isSecret: p.isSecret,
          validationJson: p.validationJson,
        })) as UpdateAgentParameterRequest[],
      };
      await agentService.updateAgent(agent.id, updatePayload);
      addNotification('Context window configuration saved', 'success');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save configuration';
      setError(message);
      addNotification(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        Context window &amp; compaction
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Max tokens, summarization, auto compaction, and image handling for this agent.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {readOnly && (
        <Alert severity="info" sx={{ mb: 4 }}>
          System agents cannot be modified. Clone this agent to customize its context window settings.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
        <TextField
          label="Max context tokens"
          type="number"
          value={form.maxContextTokens}
          onChange={(e) => setForm((s) => ({ ...s, maxContextTokens: Number(e.target.value) }))}
          error={form.maxContextTokens < 1024}
          helperText={form.maxContextTokens < 1024 ? 'Must be at least 1024' : ' '}
          disabled={readOnly}
          fullWidth
        />

        <TextField
          label="Summarization threshold"
          type="number"
          value={form.summarizationThreshold}
          onChange={(e) => setForm((s) => ({ ...s, summarizationThreshold: Number(e.target.value) }))}
          error={form.summarizationThreshold < 1 || form.summarizationThreshold > form.maxContextTokens}
          helperText={
            form.summarizationThreshold < 1 || form.summarizationThreshold > form.maxContextTokens
              ? 'Must be between 1 and Max context tokens'
              : ' '
          }
          disabled={readOnly}
          fullWidth
        />

        <FormControl fullWidth disabled={readOnly}>
          <InputLabel id="strategy-label">Strategy</InputLabel>
          <Select
            labelId="strategy-label"
            label="Strategy"
            value={form.summarizationStrategy}
            onChange={(e) =>
              setForm((s) => ({ ...s, summarizationStrategy: Number(e.target.value) as any }))
            }
          >
            <MenuItem value={ContextSummarizationStrategy.LastXMinusY}>Last X minus Y</MenuItem>
            <MenuItem value={ContextSummarizationStrategy.SlidingWindow}>Sliding window</MenuItem>
            <MenuItem value={ContextSummarizationStrategy.ImportanceBased}>Importance based</MenuItem>
            <MenuItem value={ContextSummarizationStrategy.TimeBased}>Time based</MenuItem>
            <MenuItem value={ContextSummarizationStrategy.Hybrid}>Hybrid</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={readOnly}>
          <InputLabel id="summarizer-type-label">Summarizer</InputLabel>
          <Select
            labelId="summarizer-type-label"
            label="Summarizer"
            value={form.summarizerType}
            onChange={(e) => setForm((s) => ({ ...s, summarizerType: Number(e.target.value) as any }))}
          >
            <MenuItem value={SummarizerType.Standard}>Standard</MenuItem>
            <MenuItem value={SummarizerType.Custom}>Custom prompt</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Messages to retain"
          type="number"
          value={form.messagesToRetain}
          onChange={(e) => setForm((s) => ({ ...s, messagesToRetain: Number(e.target.value) }))}
          disabled={readOnly}
          fullWidth
        />

        <TextField
          label="Messages to summarize"
          type="number"
          value={form.messagesToSummarize}
          onChange={(e) => setForm((s) => ({ ...s, messagesToSummarize: Number(e.target.value) }))}
          disabled={readOnly}
          fullWidth
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Auto compaction
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When on, chat history is automatically summarized when the context exceeds the threshold below, so long sessions stay within limits. When off, no compaction runs.
      </Typography>
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={
            <Switch
              checked={form.enableAutoSummarization}
              onChange={(e) => setForm((s) => ({ ...s, enableAutoSummarization: e.target.checked }))}
              disabled={readOnly}
            />
          }
          label={form.enableAutoSummarization ? 'Auto compaction on' : 'Auto compaction off'}
        />
      </Box>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
        Image context
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Controls how image attachments are turned into text for the model (layout/OCR, AI description, omitted, or native vision without preprocessing).
      </Typography>
      <Box sx={{ mb: 4, maxWidth: 480 }}>
        <FormControl fullWidth disabled={readOnly}>
          <InputLabel id="image-context-mode-label">Image handling</InputLabel>
          <Select
            labelId="image-context-mode-label"
            label="Image handling"
            value={imageContextMode}
            onChange={(e) => setImageContextMode(e.target.value as AgentImageContextMode)}
          >
            <MenuItem value="ocrToContext">OCR / layout text</MenuItem>
            <MenuItem value="descriptionToContext">AI image description</MenuItem>
            <MenuItem value="ignoreImage">Ignore attachments</MenuItem>
            <MenuItem value="visionOnlyNoCompaction">Vision only (no preprocessing)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          Compression ratio
        </Typography>
        <Slider
          value={form.compressionRatio}
          min={0.05}
          max={0.95}
          step={0.05}
          valueLabelDisplay="auto"
          disabled={readOnly}
          onChange={(_, value) => setForm((s) => ({ ...s, compressionRatio: Number(value) }))}
        />
        <Typography variant="caption" color="text.secondary">
          Target: {(form.compressionRatio * 100).toFixed(0)}% of original content
        </Typography>
      </Box>

      {form.summarizerType === SummarizerType.Custom && (
        <Box sx={{ mb: 4 }}>
          <TextField
            label="Custom summarizer prompt"
            value={form.customSummarizerPrompt ?? ''}
            onChange={(e) => setForm((s) => ({ ...s, customSummarizerPrompt: e.target.value }))}
            error={!form.customSummarizerPrompt?.trim()}
            helperText={!form.customSummarizerPrompt?.trim() ? 'Required when using Custom prompt' : ' '}
            disabled={readOnly}
            multiline
            minRows={6}
            fullWidth
          />
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <TextField
          label="Priority messages JSON (optional)"
          value={form.priorityMessagesJson ?? ''}
          onChange={(e) => setForm((s) => ({ ...s, priorityMessagesJson: e.target.value }))}
          disabled={readOnly}
          multiline
          minRows={3}
          fullWidth
        />
      </Box>

      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!canSave || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AgentContextWindowPanel;
